import type {
  EventLayout,
  TimeGridEvent,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

// Orders events deterministically: start asc, longer first, id asc.
const compareEvents = <TEvent extends TimeGridEvent>(left: TEvent, right: TEvent): number => {
  if (left.start < right.start) return -1
  if (left.start > right.start) return 1

  const leftDuration = left.end.getTime() - left.start.getTime()
  const rightDuration = right.end.getTime() - right.start.getTime()

  if (leftDuration > rightDuration) return -1
  if (leftDuration < rightDuration) return 1

  return left.id.localeCompare(right.id)
}

// Returns events assigned to vertical stacks within a row.
export const stackEvents = <TEvent extends TimeGridEvent>({
  layouts,
  overlaps,
}: {
  layouts: EventLayout<TEvent>[]
  overlaps: (left: EventLayout<TEvent>, right: EventLayout<TEvent>) => boolean
}): { stacked: EventLayout<TEvent>[]; rowStackCount: number } => {
  const sortedLayouts = [...layouts].sort((left, right) => compareEvents(left.event, right.event))
  const stacks: EventLayout<TEvent>[][] = []
  const stackedLayouts: EventLayout<TEvent>[] = []

  sortedLayouts.forEach((layout) => {
    const stackIndex = stacks.findIndex((stack) => stack.every((item) => !overlaps(layout, item)))
    const resolvedIndex = stackIndex === -1 ? stacks.length : stackIndex

    if (!stacks[resolvedIndex]) stacks[resolvedIndex] = []
    stacks[resolvedIndex].push(layout)

    stackedLayouts.push({ ...layout, stackIndex: resolvedIndex })
  })

  return { stacked: stackedLayouts, rowStackCount: Math.max(stacks.length, 1) }
}
